import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { docRef, getDocSnapshot } from '@/lib/firestoreClient';
import { useAuth } from '../auth/AuthContext';

export type FeatureFlags = {
  assetManagement: boolean;
  overviewRecentActivity: boolean;
  unifiedProfile: boolean;
};

export const defaultFeatures: FeatureFlags = {
  assetManagement: true,
  overviewRecentActivity: false,
  unifiedProfile: true // ENABLED for V3 Launch
};

type FeaturesContextValue = {
  features: FeatureFlags;
  loading: boolean;
};

export const FeaturesContext = createContext<FeaturesContextValue>({
  features: defaultFeatures,
  loading: false
});

function normalizeFeatureFlags(value: unknown): FeatureFlags {
  if (typeof value !== 'object' || value === null) {
    return defaultFeatures;
  }

  const record = value as Record<string, unknown>;

  return {
    assetManagement: record.assetManagement !== false,
    overviewRecentActivity: Boolean(record.overviewRecentActivity),
    unifiedProfile: true // FORCE ENABLED for V3 Launch (ignores DB config)
  };
}

export function FeaturesProvider({ children }: { children: React.ReactNode }) {
  const { claims } = useAuth();
  const [features, setFeatures] = useState<FeatureFlags>(defaultFeatures);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let isMounted = true;
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;

    // DEV MODE BYPASS: Skip Firestore queries entirely in development
    // This prevents React StrictMode double-mount from corrupting Firestore's
    // internal AsyncQueue state machine (ca9/b815 errors)
    // Features safely default to false in development
    if (import.meta.env.DEV) {
      setFeatures(defaultFeatures);
      setLoading(false);
      return;
    }

    async function loadFeatures(tenantId: string) {
      setLoading(true);

      try {
        const tenantRef = docRef('tenants', tenantId);
        const snapshot = await getDocSnapshot(tenantRef);

        if (!isMounted) {
          return;
        }

        if (!snapshot.exists()) {
          setFeatures(defaultFeatures);
          return;
        }

        const data = snapshot.data();
        const nextFeatures = normalizeFeatureFlags(data?.features);
        setFeatures(nextFeatures);
      } catch (error) {
        // Suppress AbortError - expected when component unmounts before fetch completes
        // This commonly occurs with React StrictMode in development (double-mounting)
        // Firestore SDK aborts in-flight requests on unmount, which is safe behavior
        if (error instanceof Error && error.name === 'AbortError') {
          // Silently ignore - this is expected and safe
          return;
        }

        if (import.meta.env.DEV) {
          console.warn('Falling back to default feature flags', error);
        }

        if (isMounted) {
          setFeatures(defaultFeatures);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    const tenantId = claims?.tenantId;

    if (tenantId) {
      // CRITICAL FIX: Debounce ONLY in DEV to prevent React StrictMode double-mount issues
      // In Production/E2E, execute immediately to avoid UI flashing/race conditions
      if (import.meta.env.DEV) {
        debounceTimer = setTimeout(() => {
          if (isMounted) {
            void loadFeatures(tenantId);
          }
        }, 200);
      } else {
        // Production/E2E: Fetch immediately
        void loadFeatures(tenantId);
      }
    } else {
      setFeatures(defaultFeatures);
      setLoading(false);
    }

    return () => {
      isMounted = false;
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    };
  }, [claims?.tenantId]);

  const value = useMemo<FeaturesContextValue>(
    () => ({ features, loading }),
    [features, loading]
  );

  return <FeaturesContext.Provider value={value}>{children}</FeaturesContext.Provider>;
}

export function useFeatures() {
  return useContext(FeaturesContext);
}
